/**
 * useFlowChatSessionAdapter — bridges one Flow chat session-store entry
 * (U3's `FlowChatSessionsProvider`) into the shapes `chat-panel-core.tsx`'s
 * shared surfaces already understand (U4, bead terra-flow-chat-multisession
 * -gvr.4).
 *
 * This is the "genuine logic" piece the bead calls out for its own unit
 * suite (colocated `__tests__/useFlowChatSessionAdapter.test.ts`) — unlike
 * the rest of this bead's chrome, it has a real input->output contract:
 *
 *   - `session` conforms to `UseKeystoneChatSessionReturn` so
 *     `useKeystoneChatPanelController`'s `externalSession` seam (this bead's
 *     one additive controller arg) can shadow its own internal
 *     `useKeystoneChatSession` call with this store-backed object —
 *     `PreChatSurface`/`KeystoneChatSurface` compose it unmodified.
 *   - `externalStream` composes the session's published stream snapshot
 *     with a `send` that is either the live host's own `send` (session is
 *     hosted and not stopped) or a resume-wrapper that un-stops the session
 *     via `resumeWithMessage` (D-H, "the send is the resume" — the full
 *     Stop-button UI is bead U5's scope; this is the minimum primitive a
 *     Stopped session's already-enabled composer needs to actually work).
 *
 * `startNew` (part of `session`) deliberately does NOT allocate a fresh
 * session when the entry it's bound to is ALREADY an untouched draft
 * (`conversationId === null`) — that is the state `handleExampleClick`'s
 * `session.startNew()` call always finds itself in (it only ever fires from
 * `PreChatSurface`'s empty state, which only renders for a draft session),
 * so treating that case as a genuine "allocate another session" would spawn
 * a redundant duplicate draft on every single example-question click. It
 * DOES allocate + focus a fresh draft session for the same account when
 * called against an already-COMMITTED session — the real "start a new chat
 * while looking at an existing one" case (D-C).
 */
import { useCallback, useMemo } from 'react';
import type { PersonaListItem } from '../../../types/personas';
import type { UseKeystoneChatSessionReturn } from '../../account-view/keystone/useKeystoneChatSession';
import type { UseConversationStreamReturn } from '../../../view-system/hooks/useConversationStream';
import {
  useFlowChatSessions,
  useFlowChatSessionList,
  useFlowChatSessionStream,
  type FlowChatSessionSummary,
} from './FlowChatSessionsProvider';

export interface FlowChatSessionAdapterResult {
  /** The store entry this adapter is bound to, or `null` if it no longer
   *  exists (shouldn't happen in practice — a viewed session is never
   *  deleted out from under its own viewer today). */
  entry: FlowChatSessionSummary | null;
  /** Conforms to `UseKeystoneChatSessionReturn` — pass as
   *  `useKeystoneChatPanelController`'s `externalSession` arg. */
  session: UseKeystoneChatSessionReturn;
  /**
   * The composed stream to hand `DurableStreamRuntimeProvider` as
   * `externalStream` — `null` until the session's host has published at
   * least once (a real conversationId with no snapshot yet is a brief
   * "connecting" window; the caller should NOT mount
   * `DurableStreamRuntimeProvider` with an `externalStream=undefined`
   * fallback in that window, or it will open a second EventSource for the
   * same conversation). Always carries a `send` once non-null — either the
   * live host's own, or the resume-wrapper (see file header) — so it is
   * directly assignable to `DurableStreamRuntimeProvider`'s
   * `UseConversationStreamReturn`-typed prop.
   */
  externalStream: UseConversationStreamReturn | null;
  /** Debounced composer draft text (Wave 2 review finding #4) — read from
   *  the entry's `draftText`, `''` if the session no longer exists. Pass as
   *  `initialDraftValue` to `PreChatSurface`/`KeystoneChatSurface`. */
  draftText: string;
  /** Persist the composer's current text (the surface debounces before
   *  calling this — see `chat-panel-core.tsx`'s `useComposerDraft`). */
  setDraftText: (text: string) => void;
}

export function useFlowChatSessionAdapter(sessionId: string): FlowChatSessionAdapterResult {
  const list = useFlowChatSessionList();
  const entry = useMemo(
    () => list.find((s) => s.sessionId === sessionId) ?? null,
    [list, sessionId],
  );
  const stream = useFlowChatSessionStream(sessionId);
  const {
    sendFirstMessage,
    pickPersona,
    createSession,
    clearCreateError,
    resumeWithMessage,
    setDraftText,
    bumpActivity,
  } = useFlowChatSessions();

  const setDraftTextCb = useCallback(
    (text: string) => setDraftText(sessionId, text),
    [setDraftText, sessionId],
  );

  const pickPersonaCb = useCallback(
    (p: PersonaListItem) => pickPersona(sessionId, p),
    [pickPersona, sessionId],
  );

  const startNew = useCallback(() => {
    if (!entry) return;
    // Already a pristine, uncommitted draft — nothing to clear (idempotent;
    // see file header for why this is NOT "allocate another session").
    if (entry.conversationId === null) return;
    createSession(entry.accountId, entry.accountName);
  }, [entry, createSession]);

  const handleSend = useCallback(
    async (text: string) => {
      sendFirstMessage(sessionId, text);
    },
    [sendFirstMessage, sessionId],
  );

  const clearCreateErrorCb = useCallback(
    () => clearCreateError(sessionId),
    [clearCreateError, sessionId],
  );

  // Inert — U3's `SessionStreamHost` is the sole flusher of a session's
  // queued first message (a synchronous store-level consume, per
  // coding-standards.md §5); nothing downstream of this adapter ever reads
  // these two fields (`PreChatSurface` doesn't render `pendingFirstMessage`,
  // only `DurableStreamRuntimeProvider` used to — and this bead's panel
  // never passes it one). Present only for `UseKeystoneChatSessionReturn`
  // conformance.
  const consumePendingFirstMessage = useCallback(() => null, []);

  const session: UseKeystoneChatSessionReturn = useMemo(
    () => ({
      persona: entry?.persona ?? null,
      pendingFirstMessage: null,
      createError: entry?.createError ?? null,
      isCreating: entry?.createStatus === 'creating',
      pickPersona: pickPersonaCb,
      startNew,
      handleSend,
      consumePendingFirstMessage,
      clearCreateError: clearCreateErrorCb,
    }),
    [entry, pickPersonaCb, startNew, handleSend, consumePendingFirstMessage, clearCreateErrorCb],
  );

  const externalStream: UseConversationStreamReturn | null = useMemo(() => {
    if (!stream) return null;
    if (stream.send) {
      const liveSend = stream.send;
      return {
        ...stream,
        // Wave 2 review finding — a send on an already-committed, already-
        // live session (a follow-up question in an existing conversation)
        // only ever advanced `lastActivityAt` on the qualified
        // `isRunning true→false` run boundary, so the session list didn't
        // move to the top while a long-running response was still
        // streaming. Bump activity at the moment the send is INITIATED,
        // not only on completion — a long-running session should read as
        // "just active" for its entire duration, not just at the end.
        send: async (text: string) => {
          bumpActivity(sessionId);
          await liveSend(text);
        },
      };
    }
    // Frozen (stopped) snapshot — the host omitted `send`. Compose a
    // resume-wrapper: un-stop the session and queue `text` as its pending
    // first message (D-H, see file header).
    return {
      ...stream,
      send: async (text: string) => {
        resumeWithMessage(sessionId, text);
      },
    };
  }, [stream, resumeWithMessage, bumpActivity, sessionId]);

  return { entry, session, externalStream, draftText: entry?.draftText ?? '', setDraftText: setDraftTextCb };
}
